"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Shield, AlertTriangle, ShieldCheck, Download } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";

import FormInput from "@/app/_module/components/FormInput";
import { Form, FormField } from "@/app/_module/components/ui/form";
import { successToast } from "@/app/_module/services/helper";
import { getApiUrl } from "@/app/_module/services/config";
import InsuraCentralLogo from "@/app/_module/components/InsuraCentralLogo";
import { useDeviceContext } from "@/app/_module/contexts/DeviceDetectionContext";
import supabaseAuthService from "@/app/_module/services/supabaseAuthService";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import { DIRECT130_BOX, DIRECT130_BUTTON } from "@/app/_module/utils/direct130Host";

import type { T2FASchema } from "../schema/signin-schema";
import { twoFASchema } from "../schema/signin-schema";

// Storage key for pending 2FA context (must match auth.ts)
const PENDING_2FA_STORAGE_KEY = "insuracentral_pending_2fa";

// Session refresh interval (every 5 minutes to keep session alive)
const SESSION_REFRESH_INTERVAL = 5 * 60 * 1000;

interface TwoFactorAuthProps {
  userId: string;
  onBack: () => void;
  onSuccess: (fallbackToken?: string) => void;
  userMfaSettings?: {
    smsEnabled?: boolean;
    totpEnabled?: boolean;
    preferredMethod?: string;
    backupCodes?: string[];  // Array from login response
    hasBackupCodes?: boolean; // Boolean from API response
  };
  /** Direct 130's address: shown inside its own sign-in page, in its look (no InsuraCentral mark). */
  direct130?: boolean;
}

// For login: Only TOTP is allowed as primary method, backup codes are emergency only
type MFAMethod = 'totp' | 'backup';

interface AvailableMethods {
  availableMethods: MFAMethod[];
  preferredMethod: string;
  hasBackupCodes: boolean;
  smsOnlyUser: boolean; // User has SMS but no TOTP - needs to set up TOTP
}

const TwoFactorAuth = function({ userId, onBack, onSuccess, userMfaSettings, direct130 }: TwoFactorAuthProps) {
  const { getResponsiveMaxWidth } = useDeviceContext();
  const { branding, isWhiteLabeled } = useBranding();
  const [selectedMethod, setSelectedMethod] = useState<MFAMethod>('totp');
  const [availableMethods, setAvailableMethods] = useState<AvailableMethods | null>(null);
  const [isLoadingMethods, setIsLoadingMethods] = useState(!userMfaSettings); // Skip loading if settings provided
  const [sessionExpired, setSessionExpired] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [showEmergencyBackup, setShowEmergencyBackup] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  // SMS-only → authenticator self-serve recovery wizard.
  const [recoveryStep, setRecoveryStep] = useState<'intro' | 'phone' | 'totp' | 'backup'>('intro');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryMaskedPhone, setRecoveryMaskedPhone] = useState('');
  const [recoveryQr, setRecoveryQr] = useState('');
  const [recoveryManualKey, setRecoveryManualKey] = useState('');
  const [recoveryBackupCodes, setRecoveryBackupCodes] = useState<string[]>([]);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');
  const [recoveryCooldown, setRecoveryCooldown] = useState(0);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const form = useForm<T2FASchema>({
    resolver: zodResolver(twoFASchema),
    mode: "onChange",
    defaultValues: {
      code: "",
    },
  });

  const {
    control,
    formState: { errors, isValid },
  } = form;

  // Get access token from pending 2FA session or current Supabase session
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      // First, try to get token from pending 2FA context (saved during login)
      if (typeof window !== "undefined") {
        const pendingRaw = sessionStorage.getItem(PENDING_2FA_STORAGE_KEY);
        if (pendingRaw) {
          const pending = JSON.parse(pendingRaw);
          if (pending?.session?.access_token) {
            return pending.session.access_token;
          }
        }
      }

      // Fallback to current Supabase session
      const session = await supabaseAuthService.getCurrentSession();
      return session?.access_token || null;
    } catch {
      return null;
    }
  }, []);

  // Keep session alive while on 2FA page
  // This prevents the session from expiring if user takes time to enter code
  useEffect(() => {
    const keepSessionAlive = async () => {
      try {
        const refreshedSession = await supabaseAuthService.refreshSession();
        if (refreshedSession) {
          // CRITICAL: Update pending 2FA storage with refreshed tokens
          // When refreshSession() rotates tokens, the pending 2FA context becomes stale.
          // verify2FACode() reads tokens from this storage, so they must stay in sync.
          try {
            const pendingRaw = sessionStorage.getItem(PENDING_2FA_STORAGE_KEY);
            if (pendingRaw) {
              const pending = JSON.parse(pendingRaw);
              pending.session = {
                ...pending.session,
                access_token: refreshedSession.access_token,
                refresh_token: refreshedSession.refresh_token,
              };
              sessionStorage.setItem(PENDING_2FA_STORAGE_KEY, JSON.stringify(pending));
            }
          } catch {
            // Silent fail - storage update is best-effort
          }
        } else {
          // Session could not be refreshed - it may have expired
          // Check if we still have pending 2FA context
          const pendingRaw = typeof window !== "undefined"
            ? sessionStorage.getItem(PENDING_2FA_STORAGE_KEY)
            : null;

          if (!pendingRaw) {
            // No pending context and no valid session - session has expired
            setSessionExpired(true);
          }
        }
      } catch {
        // Refresh failed silently - will retry on next interval
      }
    };

    // DO NOT refresh immediately on mount!
    // The session was JUST created by signInWithPassword() moments ago.
    // Calling refreshSession() immediately rotates the tokens, which can invalidate
    // the pending 2FA context tokens in a multi-device scenario.
    // Only start the periodic refresh after the first interval (5 minutes).
    refreshIntervalRef.current = setInterval(keepSessionAlive, SESSION_REFRESH_INTERVAL);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  // Initialize MFA methods from props or fetch from API
  // LOGIN ONLY ALLOWS: TOTP (primary) or Backup Codes (emergency only)
  // SMS-only users must set up TOTP before they can log in
  useEffect(() => {
    const initializeMfaMethods = async () => {
      // Hoisted so EVERY path below — including the catch fallback — can see it.
      // The login response already tells us whether this user is SMS-only; losing
      // that on a failed fetch is what stranded them (see the catch block).
      const propsSmsOnly =
        userMfaSettings?.smsEnabled === true && !userMfaSettings?.totpEnabled;

      // If MFA settings were passed as props (from login response), use them directly
      if (userMfaSettings) {
        // Check if user has SMS but no TOTP - they need to set up TOTP
        const smsOnlyUser = propsSmsOnly;

        // For login: Only allow TOTP (NOT SMS)
        const loginMethods: MFAMethod[] = [];

        if (userMfaSettings.totpEnabled) {
          loginMethods.push('totp');
        }
        // SMS is NOT allowed for login - users must use TOTP or backup codes

        // Check for backup codes: can be either boolean (hasBackupCodes) or array (backupCodes)
        const hasBackup = userMfaSettings.hasBackupCodes === true || (userMfaSettings.backupCodes?.length || 0) > 0;

        // ⚠️ ABSENT is not NO. Login responses strip the backupCodes array
        // (rightly — codes don't belong in the wire payload) and often don't
        // carry the hasBackupCodes flag either. Trusting that silence hid the
        // emergency-backup entry for users who DO have codes (the phone flow
        // showed no way in for anyone who lost their authenticator). Only
        // early-return when the prop gives a POSITIVE signal or an explicit
        // false; otherwise fall through to /mfa/available-methods, which knows.
        if (hasBackup || userMfaSettings.hasBackupCodes === false) {
          const methods: AvailableMethods = {
            availableMethods: loginMethods,
            preferredMethod: 'totp', // Always prefer TOTP for login
            hasBackupCodes: hasBackup,
            smsOnlyUser,
          };

          setAvailableMethods(methods);

          // Select initial method - TOTP only (backup is emergency)
          if (loginMethods.includes('totp')) {
            setSelectedMethod('totp');
          }

          setIsLoadingMethods(false);
          return;
        }
      }

      // Otherwise, try to fetch from API (fallback for edge cases)
      try {
        setIsLoadingMethods(true);

        const rawApiUrl = process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_BACKEND_URL || process.env.NEXT_PUBLIC_API_BASE_URL || 'https://main-fex-platform-service.onrender.com/api';
        // Prod env vars carry the bare host without /api — every sign-in was
        // 404ing this fallback fetch (and silently mis-defaulting the method
        // list). Normalize to a base that always ends in /api.
        const trimmed = rawApiUrl.replace(/\/+$/, '');
        const apiUrl = trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;

        // Use our helper to get token from pending session or current session
        const token = await getAccessToken();

        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await fetch(`${apiUrl}/mfa/available-methods`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        // Handle 401 specifically - session expired
        if (response.status === 401) {
          setSessionExpired(true);
          throw new Error('Session expired');
        }

        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }

        const data = await response.json();

        if (data?.data) {
          // Check if user has SMS but no TOTP
          const hasSms = (data.data.availableMethods || []).includes('sms');
          const hasTotp = (data.data.availableMethods || []).includes('totp');
          // `hasSms` comes from /mfa/available-methods, which only reports 'sms'
          // when smsEnabled AND users.contact_phone is non-empty. contact_phone is
          // rewritten by the auth-sync trigger on every login and has been seen
          // blanking and refilling minutes apart, so an SMS-only user can arrive
          // here with an EMPTY method list and get read as "not SMS-only" — the
          // bare TOTP prompt again. Trust the login response as well: if either
          // source says SMS-only and neither sees TOTP, offer the set-up flow.
          const smsOnlyUser = (hasSms || propsSmsOnly) && !hasTotp;

          // Filter out SMS for login - only allow TOTP
          const filteredMethods: MFAMethod[] = [];
          if (hasTotp) {
            filteredMethods.push('totp');
          }

          setAvailableMethods({
            availableMethods: filteredMethods,
            preferredMethod: 'totp',
            hasBackupCodes: data.data.hasBackupCodes || false,
            smsOnlyUser,
          });

          // Select TOTP if available
          if (filteredMethods.includes('totp')) {
            setSelectedMethod('totp');
          }
        } else {
          throw new Error('Invalid API response format');
        }
      } catch (error) {
        // Keep the form usable when /mfa/available-methods can't be reached —
        // but NEVER invent `smsOnlyUser: false` here.
        //
        // An SMS-only user has no authenticator to read a code from, and login
        // deliberately refuses SMS as a factor, so forcing false rendered them a
        // TOTP prompt for an app they never enrolled: no code, no resend, no way
        // in except a backup code they may not have kept. Observed on prod
        // 2026-09-20 — an agent hit Verify five times in 16 seconds against a
        // challenge that had never sent him anything, and reported "I never got
        // the code". He was right: the page never asked for one.
        //
        // The login response already told us the truth, so fall back to THAT and
        // let the smsOnlyUser branch offer the set-up-an-authenticator flow.
        setAvailableMethods({
          availableMethods: ['totp'],
          preferredMethod: 'totp',
          hasBackupCodes: true,
          smsOnlyUser: propsSmsOnly,
        });
        setSelectedMethod('totp');
      } finally {
        setIsLoadingMethods(false);
      }
    };

    void initializeMfaMethods();
  }, [userId, userMfaSettings, getAccessToken]);

  const onSubmit = async (values: T2FASchema) => {
    setCodeError(null);
    setIsVerifying(true);

    // Read the pending 2FA token BEFORE verify2FACode runs
    let savedToken: string | undefined;
    try {
      const pendingRaw = sessionStorage.getItem(PENDING_2FA_STORAGE_KEY);
      if (pendingRaw) {
        const pending = JSON.parse(pendingRaw);
        savedToken = pending?.session?.access_token;
      }
    } catch {
      // Silent fail
    }

    try {
      // Call same-origin proxied path (Next.js rewrite -> backend, no CORS)
      //
      // This is a raw fetch, so it does NOT pass through the axios interceptor that
      // attaches x-device-id to every other request. Without the header the backend
      // falls back to a derived id (`${userId}-${browser}-${os}`), registers the
      // session as an "unidentified device", and then the first axios call that DOES
      // carry the real id looks like a different device. Send it here too so the
      // session is born with the same identity it will be checked against.
      let deviceIdHeader: Record<string, string> = {};
      try {
        const deviceId = localStorage.getItem('insuracentral_device_id');
        if (deviceId) deviceIdHeader = { 'X-Device-Id': deviceId };
      } catch {
        // localStorage unavailable — backend falls back safely.
      }

      // MFA API GATE (2026-08-13): send the PENDING Supabase access token as a bearer.
      // The backend keys its "this session completed MFA" marker on the session_id claim
      // inside this token; without it the marker is never stamped and every MFA user is
      // locked out of every data endpoint after a *successful* verification.
      //
      // ⚠️ The equivalent header was added to verify2FACode() in services/auth.ts — but
      // THIS component never calls that function, it does its own raw fetch. That gap
      // took prod down on 2026-08-13. `savedToken` is read above; use it here.
      const authHeader: Record<string, string> = savedToken
        ? { Authorization: `Bearer ${savedToken}` }
        : {};

      const res = await fetch('/api/auth/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...deviceIdHeader, ...authHeader },
        // supabaseAccessToken in the BODY as well as the header: this request goes
        // through a Next.js rewrite (next.config.ts -> /api/auth/2fa/verify), and a
        // proxy hop is exactly where an Authorization header can get dropped. The
        // backend reads the header first and falls back to this.
        body: JSON.stringify({ userId, code: values.code, supabaseAccessToken: savedToken }),
      });
      if (!res.ok) throw new Error('invalid');

      // If this login is part of a "Sign in with InsuraCentral" (OIDC) flow,
      // capture the post-MFA HS256 access token and jump STRAIGHT to the bridge,
      // bypassing the normal agency-selection / dashboard redirect (the SSO flow
      // doesn't need an agency — it only needs the user's identity + NPN).
      try {
        if (sessionStorage.getItem('oidc_sso_pending') === '1') {
          const v = await res.clone().json().catch(() => null);
          // Deep-search for the backend HS256 access token at any nesting depth
          // (robust to the global TransformInterceptor wrapping). Prefer the
          // `accessToken` key (HS256, carries userId) over supabaseAccessToken.
          const findToken = (o: any, depth = 0): string | null => {
            if (!o || typeof o !== 'object' || depth > 5) return null;
            if (typeof o.accessToken === 'string' && o.accessToken.split('.').length === 3) return o.accessToken;
            for (const k of Object.keys(o)) {
              const r = findToken(o[k], depth + 1);
              if (r) return r;
            }
            return null;
          };
          const t = findToken(v);
          // Re-validate the stored target is a same-origin /oauth/bridge URL
          // before navigating (defense against a tampered sessionStorage value).
          let nextUrl = '';
          try {
            const u = new URL(sessionStorage.getItem('oidc_sso_next') || '', window.location.origin);
            if (u.origin === window.location.origin && u.pathname.startsWith('/oauth/bridge')) {
              nextUrl = u.toString();
            }
          } catch { /* ignore */ }
          if (t && nextUrl) {
            sessionStorage.setItem('oidc_sso_token', t);
            setIsVerifying(false);
            window.location.href = nextUrl; // → /oauth/bridge, completes the grant
            return;
          }
        }
      } catch { /* non-fatal */ }

      // Code is valid — session already created by the proxied call above
      setIsVerifying(false);
      successToast("2FA verification successful!");

      onSuccess(savedToken);
    } catch {
      setIsVerifying(false);
      setCodeError("Incorrect code. Please try again.");
      form.reset({ code: "" });
    }
  };

  // ── SMS-only → authenticator recovery handlers ──
  // Use getApiUrl() (direct backend URL incl. the /api prefix) — the same helper
  // the working signup MFA setup uses. There is NO general /api/* Next rewrite,
  // and the earlier raw-env base dropped /api → "Cannot POST /auth/mfa/...".
  const recoveryFetch = async (path: string, body?: any) => {
    const token = await getAccessToken();
    if (!token) { setSessionExpired(true); throw new Error('Session expired'); }
    const res = await fetch(getApiUrl(`/auth/mfa/${path}`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const json = await res.json().catch(() => null);
    return { ok: res.ok, json, data: json?.data ?? json };
  };

  const handleRecoverySend = async () => {
    setRecoveryError(''); setRecoveryLoading(true);
    try {
      const { ok, json, data } = await recoveryFetch('sms-recovery/send');
      if (!ok || !data?.sent) throw new Error(json?.message || 'Failed to send code');
      setRecoveryMaskedPhone(data.maskedPhone || 'your phone');
      setRecoveryStep('phone');
      setRecoveryCooldown(60);
    } catch (e: any) {
      setRecoveryError(e?.message || 'Could not send the code. Please try again.');
    } finally { setRecoveryLoading(false); }
  };

  const handleRecoveryVerifyPhone = async () => {
    if (!recoveryCode.trim()) return;
    setRecoveryError(''); setRecoveryLoading(true);
    try {
      const { ok, json, data } = await recoveryFetch('sms-recovery/verify-phone', { code: recoveryCode.trim() });
      if (!ok || !data?.verified) throw new Error(json?.message || 'Invalid or expired code');
      setRecoveryQr(data.qrCodeUrl || '');
      setRecoveryManualKey(data.manualEntryKey || '');
      setRecoveryCode('');
      setRecoveryStep('totp');
    } catch (e: any) {
      setRecoveryError(e?.message || 'Invalid or expired code. Please try again.');
    } finally { setRecoveryLoading(false); }
  };

  const handleRecoveryEnable = async () => {
    if (!recoveryCode.trim()) return;
    setRecoveryError(''); setRecoveryLoading(true);
    try {
      const { ok, json, data } = await recoveryFetch('sms-recovery/enable', { code: recoveryCode.trim() });
      if (!ok || !data?.enabled) throw new Error(json?.message || 'Invalid authenticator code');
      setRecoveryBackupCodes(Array.isArray(data.backupCodes) ? data.backupCodes : []);
      setRecoveryCode('');
      setRecoveryStep('backup');
    } catch (e: any) {
      setRecoveryError(e?.message || 'Invalid authenticator code. Please try again.');
    } finally { setRecoveryLoading(false); }
  };

  // After setup: flip to the normal TOTP login challenge so they finish signing in.
  const handleRecoveryFinish = () => {
    setAvailableMethods({ availableMethods: ['totp'], preferredMethod: 'totp', hasBackupCodes: true, smsOnlyUser: false });
    setSelectedMethod('totp');
    setRecoveryError('');
  };

  useEffect(() => {
    if (recoveryCooldown > 0) {
      const t = setTimeout(() => setRecoveryCooldown(recoveryCooldown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [recoveryCooldown]);

  // The product's mark over each step. Direct 130's page already has its wordmark above the card.
  const brandHeader = direct130 ? null : (
    <div className="flex items-center gap-2">
      <InsuraCentralLogo size={32} />
      {isWhiteLabeled ? (
        <span className="text-3xl font-bold text-primary">{branding.brandName}</span>
      ) : (
        <span className="text-3xl font-bold">
          <span className="text-[#0a2540]">Insura</span>
          <span className="text-[#0096c7]">Central</span>
        </span>
      )}
    </div>
  );

  const switchToBackupCode = () => {
    setShowEmergencyBackup(true);
    setSelectedMethod('backup');
    form.reset({ code: '' });
    setCodeError(null);
  };
  const switchToAuthenticator = () => {
    setShowEmergencyBackup(false);
    setSelectedMethod('totp');
    form.reset({ code: '' });
    setCodeError(null);
  };

  // Show session expired message
  if (sessionExpired) {
    return (
      <div className={`w-full ${getResponsiveMaxWidth("lg")} mx-auto space-y-8`}>
        {brandHeader}
        <div className="space-y-4 text-center py-8">
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
            <Shield className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Session Expired</h2>
          <p className="text-gray-600 max-w-md mx-auto">
            Your session has expired due to inactivity. Please sign in again to continue.
          </p>
          <button
            type="button"
            onClick={onBack}
            className="mt-4 inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while fetching methods
  if (isLoadingMethods) {
    return (
      <div className={`w-full ${getResponsiveMaxWidth("lg")} mx-auto space-y-8`}>
        {brandHeader}
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
        <p className="text-center text-gray-600">Loading authentication methods...</p>
      </div>
    );
  }

  // SMS-only users: self-serve onto an authenticator. We prove they still own
  // the phone on file (one-time SMS code) BEFORE letting them set up TOTP, so
  // this can't be used as a password-only bypass of their SMS second factor.
  if (availableMethods?.smsOnlyUser) {
    const downloadBackupCodes = () => {
      const blob = new Blob([recoveryBackupCodes.join('\n')], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'backup-codes.txt';
      a.click();
      URL.revokeObjectURL(url);
    };
    return (
      <div className={`w-full ${getResponsiveMaxWidth("lg")} mx-auto space-y-6`}>
        {brandHeader}

        {/* STEP 1 — intro: confirm identity via SMS to the phone on file */}
        {recoveryStep === 'intro' && (
          <div className="space-y-4 text-center py-4">
            <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Set up an authenticator app</h2>
            <p className="text-gray-600 max-w-md mx-auto">
              For security, signing in now uses an authenticator app — we recommend <strong>Google Authenticator</strong> (Authy or Apple Passwords also work). Let&apos;s switch you over — it takes about a minute.
            </p>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              First, we&apos;ll text a code to the phone on your account to confirm it&apos;s you.
            </p>
            {recoveryError && <p className="text-sm text-red-600">{recoveryError}</p>}
            <button
              type="button"
              onClick={handleRecoverySend}
              disabled={recoveryLoading}
              className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white font-medium py-3 px-6 rounded-lg transition-all"
            >
              {recoveryLoading ? 'Sending…' : 'Text me a code'}
            </button>
            {availableMethods.hasBackupCodes && (
              <div className="pt-4 border-t border-gray-200 mt-4">
                <p className="text-sm text-gray-500 mb-2">Have a backup code saved? Use one to sign in this time:</p>
                <button
                  type="button"
                  onClick={() => {
                    setAvailableMethods({ ...availableMethods, smsOnlyUser: false, availableMethods: [] });
                    setSelectedMethod('backup');
                    setShowEmergencyBackup(true);
                  }}
                  className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-medium text-sm"
                >
                  <AlertTriangle className="w-4 h-4" /> Use Emergency Backup Code
                </button>
              </div>
            )}
            <div>
              <button type="button" onClick={onBack} className="mt-2 inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm">
                <ArrowLeft className="w-4 h-4" /> Back to Sign In
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 — verify phone ownership */}
        {recoveryStep === 'phone' && (
          <div className="space-y-4 text-center py-4">
            <h2 className="text-2xl font-bold text-gray-900">Confirm it&apos;s you</h2>
            <p className="text-gray-600 max-w-md mx-auto">
              Enter the code we texted to <strong>{recoveryMaskedPhone}</strong>.
            </p>
            <input
              inputMode="numeric"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="Enter code"
              className="w-full max-w-xs mx-auto text-center text-lg tracking-widest border border-gray-300 rounded-lg py-3 px-4 focus:ring-2 focus:ring-primary focus:border-primary"
            />
            {recoveryError && <p className="text-sm text-red-600">{recoveryError}</p>}
            <div>
              <button
                type="button"
                onClick={handleRecoveryVerifyPhone}
                disabled={recoveryLoading || !recoveryCode.trim()}
                className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white font-medium py-3 px-6 rounded-lg transition-all"
              >
                {recoveryLoading ? 'Verifying…' : 'Verify'}
              </button>
            </div>
            <button
              type="button"
              onClick={handleRecoverySend}
              disabled={recoveryCooldown > 0 || recoveryLoading}
              className="text-sm text-primary hover:underline disabled:text-gray-400 disabled:no-underline"
            >
              {recoveryCooldown > 0 ? `Resend in ${recoveryCooldown}s` : 'Resend code'}
            </button>
          </div>
        )}

        {/* STEP 3 — scan QR + enter authenticator code */}
        {recoveryStep === 'totp' && (
          <div className="space-y-4 text-center py-4">
            <h2 className="text-2xl font-bold text-gray-900">Scan the QR code</h2>
            <p className="text-gray-600 max-w-md mx-auto">
              Open your authenticator app (we recommend <strong>Google Authenticator</strong>) and scan this, then enter the 6-digit code it shows.
            </p>
            {recoveryQr && (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={recoveryQr} alt="Authenticator QR code" className="w-48 h-48 mx-auto border rounded-lg p-2 bg-white" />
            )}
            {recoveryManualKey && (
              <p className="text-xs text-gray-500">Can&apos;t scan? Enter this key: <span className="font-mono font-medium text-gray-700 break-all">{recoveryManualKey}</span></p>
            )}
            <input
              inputMode="numeric"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code"
              className="w-full max-w-xs mx-auto text-center text-lg tracking-widest border border-gray-300 rounded-lg py-3 px-4 focus:ring-2 focus:ring-primary focus:border-primary"
            />
            {recoveryError && <p className="text-sm text-red-600">{recoveryError}</p>}
            <div>
              <button
                type="button"
                onClick={handleRecoveryEnable}
                disabled={recoveryLoading || recoveryCode.trim().length < 6}
                className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 disabled:opacity-60 text-white font-medium py-3 px-6 rounded-lg transition-all"
              >
                {recoveryLoading ? 'Enabling…' : 'Enable authenticator'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 — save backup codes, then finish sign-in */}
        {recoveryStep === 'backup' && (
          <div className="space-y-4 text-center py-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <ShieldCheck className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Authenticator set up!</h2>
            <p className="text-gray-600 max-w-md mx-auto">
              Save these backup codes somewhere safe — each works once if you ever lose your authenticator.
            </p>
            {recoveryBackupCodes.length > 0 && (
              <div className="grid grid-cols-2 gap-2 max-w-xs mx-auto bg-gray-50 border border-gray-200 rounded-lg p-4 font-mono text-sm">
                {recoveryBackupCodes.map((c) => <div key={c}>{c}</div>)}
              </div>
            )}
            {recoveryBackupCodes.length > 0 && (
              <button type="button" onClick={downloadBackupCodes} className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                <Download className="w-4 h-4" /> Download codes
              </button>
            )}
            <div>
              <button
                type="button"
                onClick={handleRecoveryFinish}
                className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white font-medium py-3 px-6 rounded-lg transition-all"
              >
                Continue to sign in
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`w-full ${getResponsiveMaxWidth("lg")} mx-auto ${direct130 ? "space-y-6" : "space-y-8"}`}>
      {/* Logo */}
      {brandHeader}

      {/* Header */}
      <div className="space-y-2">
        <h2 className={direct130 ? "!mb-0 !text-[26px] !font-normal !leading-tight" : "text-3xl font-bold text-gray-900"}>
          {direct130 ? "Verify it’s you" : "Two-Factor Authentication"}
        </h2>
        <p className={direct130 ? "text-[15px] text-[#566465]" : "text-gray-600"}>
          {showEmergencyBackup || selectedMethod === 'backup'
            ? "Enter one of your emergency backup codes. Each code can only be used once."
            : "Enter the 6-digit code from your authenticator app."}
        </p>
      </div>

      {/* Verification Form for TOTP or Backup codes */}
      <Form {...form}>
        <form
          className="space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            const code = form.getValues('code');
            if (code && !isVerifying) {
              onSubmit({ code });
            }
          }}
        >
          {/* Code Error Message */}
          {codeError && (
            <div className="text-red-600 text-sm font-medium bg-red-50 border border-red-200 rounded-lg px-4 py-2">
              {codeError}
            </div>
          )}

          {/* Verification Code Field */}
          <FormField
            control={control}
            name="code"
            render={({ field }) => (
              <FormInput
                required
                containerClass="space-y-2"
                error={errors.code}
                id="code"
                label={showEmergencyBackup || selectedMethod === 'backup' ? "Emergency Backup Code" : "Verification Code"}
                maxLength={showEmergencyBackup || selectedMethod === 'backup' ? 32 : 6}
                placeholder={
                  showEmergencyBackup || selectedMethod === 'backup'
                    ? "Enter your 8-character backup code"
                    : "Enter 6-digit code from app"
                }
                type="text"
                // Numeric keypad for TOTP/SMS codes; full keyboard only for
                // alphanumeric backup codes. one-time-code lets iOS offer the
                // arriving SMS code right above the keyboard — the single
                // biggest quality-of-life win on a native 2FA screen.
                inputMode={showEmergencyBackup || selectedMethod === 'backup' ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                {...field}
                onChange={(e) => {
                  // Clear code error when user starts typing
                  if (codeError) setCodeError(null);

                  // For backup codes: allow alphanumeric, auto-uppercase
                  // For TOTP: only digits
                  const value = e.target.value;
                  if (showEmergencyBackup || selectedMethod === 'backup') {
                    field.onChange(value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
                  } else {
                    field.onChange(value.replace(/\D/g, ''));
                  }
                }}
                onPaste={(e) => {
                  // Clear code error on paste
                  if (codeError) setCodeError(null);

                  // Handle paste explicitly to ensure codes from authenticator apps work
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData('text');

                  let cleanedValue: string;
                  if (showEmergencyBackup || selectedMethod === 'backup') {
                    // Backup codes: keep alphanumeric, uppercase
                    cleanedValue = pastedText.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 32);
                  } else {
                    // TOTP: extract only digits, strip spaces/dashes, limit to 6
                    cleanedValue = pastedText.replace(/\D/g, '').slice(0, 6);
                  }

                  field.onChange(cleanedValue);
                }}
              />
            )}
          />

          {/* Verify Button */}
          <button
            className={direct130
              ? `${DIRECT130_BUTTON} disabled:cursor-not-allowed disabled:opacity-50`
              : "w-full bg-slate-800 hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 group"}
            disabled={!isValid || isVerifying}
            type="submit"
          >
            <span>
              {isVerifying ? "Verifying..." : direct130 ? "Verify" : "Verify Code"}
            </span>
          </button>
        </form>
      </Form>

      {/* Emergency Backup Code Section — ALWAYS offered on the TOTP screen.
          It used to be gated on hasBackupCodes, but that signal comes from
          three unreliable sources (login payload, /mfa/available-methods,
          error-fallback default) and they disagreed between desktop and the
          shell — hiding the ONLY emergency door for users who do have codes.
          A user without codes just gets a clear verification failure. */}
      {!showEmergencyBackup && selectedMethod === 'totp' && direct130 && (
        <button type="button" onClick={switchToBackupCode} className={`${DIRECT130_BOX} w-full`}>
          Lost your authenticator? Use a backup code
        </button>
      )}
      {!showEmergencyBackup && selectedMethod === 'totp' && !direct130 && (
        <div className="pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={switchToBackupCode}
            className="w-full flex flex-col items-center justify-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50 font-medium py-3 px-4 rounded-lg transition-all duration-200 border border-red-200 hover:border-red-300"
          >
            <span className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Lost access to the authenticator app?
            </span>
            <span className="text-sm">Click here to use emergency backup code</span>
          </button>
          <p className="text-xs text-gray-500 text-center mt-2">
            Backup codes are for emergency use only. Each code can only be used once.
          </p>
        </div>
      )}

      {/* Back to TOTP from Backup */}
      {showEmergencyBackup && availableMethods?.availableMethods.includes('totp') && direct130 && (
        <button type="button" onClick={switchToAuthenticator} className={`${DIRECT130_BOX} w-full`}>
          Use your authenticator app instead
        </button>
      )}
      {showEmergencyBackup && availableMethods?.availableMethods.includes('totp') && !direct130 && (
        <div className="pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={switchToAuthenticator}
            className="w-full flex items-center justify-center gap-2 text-primary hover:text-primary/80 hover:bg-primary/5 font-medium py-3 px-4 rounded-lg transition-all duration-200 border border-primary/20 hover:border-primary/30"
          >
            <Shield className="w-4 h-4" />
            Use authenticator app instead
          </button>
        </div>
      )}

      {/* Back Section */}
      <div className="text-center space-y-4">
        {/* Back to Login */}
        <button
          className={direct130 ? "mx-auto rounded-sm text-[15px] text-[#566465] underline-offset-4 hover:underline focus-visible:!outline-solid focus-visible:!outline-2 focus-visible:!outline-offset-2 focus-visible:!outline-[#236b70]" : "flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors mx-auto"}
          onClick={onBack}
          type="button"
        >
          {!direct130 && <ArrowLeft className="w-4 h-4" />}
          {direct130 ? "Back to sign in" : "Back to Login"}
        </button>
      </div>
    </div>
  );
}

export default TwoFactorAuth; 
